import React, { useMemo, useState, useEffect, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, useGallery, useTokens } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';
import { pickHighestContrast, contrastRatio, relativeLuminance } from '../utils/luminance';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
  sqftRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedrooms',
    three: '3 Bedrooms',
    four: '4 Bedrooms',
    five: '5 Bedrooms',
  };
  return labels[key] || `${key} Bedrooms`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  return `From $${min.toLocaleString()}`;
}

function formatSqftRange(plans: Floorplan[]): string {
  const sqfts = plans
    .flatMap((p) => [p.floorplan_sqft_min, p.floorplan_sqft_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!sqfts.length) return '';
  const min = Math.min(...sqfts);
  return `From ${min.toLocaleString()} sq ft`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
      sqftRange: formatSqftRange(plans),
    };
  });
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'floor', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Strips query params and trailing slashes so CDN variants of
 * the same image (e.g. ?width=400 vs ?width=800) are treated as one.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

/**
 * Picks the best interior photo gallery indices for the floorplan cards.
 * Returns indices into the original gallery array so they can be used
 * as `defaultIndex` in slot group sourceBindings.
 */
function pickInteriorPhotoIndices(gallery: PhotoGallery[], count: number): number[] {
  if (!gallery.length) return Array.from({ length: count }, (_, i) => i);

  // Score every gallery item for "interior" relevance, deduplicate by URL
  const seen = new Set<string>();
  const scored: { originalIndex: number; score: number }[] = [];
  for (let i = 0; i < gallery.length; i++) {
    const photo = gallery[i];
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    scored.push({ originalIndex: i, score: scoreInterior(photo) });
  }

  // Sort by score descending
  scored.sort((a, b) => b.score - a.score);

  // Take the top-scored unique photos, then spread them out to avoid
  // visually similar adjacent photos (e.g. 3 kitchen shots in a row).
  const pool = scored.slice(0, Math.max(count * 2, 8));
  if (pool.length <= count) {
    const indices = pool.map((p) => p.originalIndex);
    // Pad with sequential indices if gallery is small
    while (indices.length < count) {
      indices.push(indices.length < gallery.length ? indices.length : 0);
    }
    return indices;
  }

  // Pick every Nth item to space them out visually
  const step = pool.length / count;
  const picked: number[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].originalIndex);
  }
  return picked;
}

/**
 * Hook that selects the 5 best interior photo gallery indices for the
 * floorplan card images. These are used as smart defaults for the
 * overridable image slot groups.
 */
function useSmartInteriorIndices(): [number, number, number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const indices = pickInteriorPhotoIndices(gallery, 5);
    return indices as [number, number, number, number, number];
  }, [gallery]);
}

// ============================================================================
// Slot group helpers for overridable card images
// ============================================================================

/** Creates a dynamic slot group definition for a card image at a given gallery index. */
function makeCardImageSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// Static slot group definitions for contentSchema (must use fixed default indices)
const cardImage0Def: SlotGroupDefinition = {
  label: 'Card 1 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage1Def: SlotGroupDefinition = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage2Def: SlotGroupDefinition = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage3Def: SlotGroupDefinition = {
  label: 'Card 4 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage4Def: SlotGroupDefinition = {
  label: 'Card 5 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

// Primary "View Availability" call-to-action link.
const ctaLinkDef: SlotGroupDefinition = {
  label: 'View Availability Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_online_leasing_url' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View Availability' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Floor plan card sub-component
// ============================================================================

interface FloorplanCardProps {
  cat: BedroomCategory;
  tinted?: boolean;
  /**
   * Provides the CSS variables `--fp-tinted-text` and `--fp-tinted-text-muted`
   * — auto-picked to hit WCAG AA against the theme's secondary color. Applied
   * on every card (not just `tinted`) so the desktop :hover state can adopt
   * the same contrast-corrected text alongside the secondary background.
   */
  tintedStyle?: React.CSSProperties;
  children: React.ReactNode; // The <EditableSlotGroup> wrapper with literal groupPath
}

function FloorplanCard({ cat, tinted, tintedStyle, children }: FloorplanCardProps) {
  // `tintedStyle` (--fp-tinted-text / --fp-tinted-text-muted) is passed on
  // every card — even non-tinted ones — so the desktop :hover CSS below can
  // swap in the tinted look without React re-renders on mouse move.
  return (
    <a
      data-reveal
      data-fp-tinted={tinted ? 'active' : 'hover'}
      href={`/floor-plans?bedroom=${cat.param}`}
      style={tintedStyle}
      className={`reveal-fade-up fp-card fp-card-item group relative flex flex-col overflow-hidden rounded-xl border transition-[background-color,border-color,box-shadow,color] duration-300 hover:shadow-lg ${
        tinted
          ? 'bg-[var(--vibe-color-secondary)] border-[var(--vibe-color-secondary)]'
          : 'bg-[var(--vibe-color-background)] border-[var(--vibe-color-primary)]/25'
      }`}
    >
      <div className="fp-card-media relative aspect-[4/3] overflow-hidden rounded-lg m-4 mb-2">
        {children}
      </div>
      <div className="flex flex-col items-center text-center px-5 pb-6 pt-2">
        {cat.sqftRange && (
          <p
            className={`fp-card-subtext font-body text-xs sm:text-sm mb-1 ${
              tinted
                ? 'text-[var(--fp-tinted-text-muted)]'
                : 'text-[var(--vibe-color-text-muted)]'
            }`}
          >
            {cat.sqftRange}
          </p>
        )}
        <h3
          className={`fp-card-title font-heading text-2xl sm:text-3xl font-bold leading-tight mb-1 ${
            tinted ? 'text-[var(--fp-tinted-text)]' : 'text-[var(--vibe-color-primary)]'
          }`}
        >
          {cat.label}
        </h3>
        <p
          className={`fp-card-subtext font-body text-sm mb-4 ${
            tinted
              ? 'text-[var(--fp-tinted-text-muted)]'
              : 'text-[var(--vibe-color-text-muted)]'
          }`}
        >
          {cat.priceRange}
        </p>
        <span className="inline-flex items-center gap-2 rounded-full bg-[var(--vibe-color-primary)] px-6 py-2.5 font-body text-sm font-semibold text-[var(--vibe-color-text-on-primary)] transition-transform duration-300 group-hover:translate-x-0.5">
          Availability
          <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">
            &rarr;
          </span>
        </span>
      </div>
    </a>
  );
}

function CardImageContent({ img, fallbackAlt }: { img: { image?: string; alt?: string }; fallbackAlt: string }) {
  return img.image ? (
    <VibesitesImage
      url={img.image}
      alt={img.alt || fallbackAlt}
      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
    />
  ) : (
    <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // Smart interior photo selection — picks the best gallery indices as defaults.
  // These are used as dynamic defaultIndex values so the slot groups get
  // intelligent defaults while remaining fully overridable by the user.
  const [idx0, idx1, idx2, idx3, idx4] = useSmartInteriorIndices();

  // Runtime slot defs with smart photo indices (may differ from static schema defaults)
  const def0 = useMemo(() => makeCardImageSlotDef('Card 1 Image', idx0), [idx0]);
  const def1 = useMemo(() => makeCardImageSlotDef('Card 2 Image', idx1), [idx1]);
  const def2 = useMemo(() => makeCardImageSlotDef('Card 3 Image', idx2), [idx2]);
  const def3 = useMemo(() => makeCardImageSlotDef('Card 4 Image', idx3), [idx3]);
  const def4 = useMemo(() => makeCardImageSlotDef('Card 5 Image', idx4), [idx4]);

  // Overridable image slots — users can select/replace these via the editor
  const img0 = useSlotGroup('floorplanslist.card_image_0', def0);
  const img1 = useSlotGroup('floorplanslist.card_image_1', def1);
  const img2 = useSlotGroup('floorplanslist.card_image_2', def2);
  const img3 = useSlotGroup('floorplanslist.card_image_3', def3);
  const img4 = useSlotGroup('floorplanslist.card_image_4', def4);

  const cta = useSlotGroup('floorplanslist.cta_link', ctaLinkDef);

  // Auto-pick a text color for the selected (tinted) card so the heading /
  // sub-text stay readable regardless of what the theme's secondary color is.
  // Prefer the theme's own on-color tokens (they usually match the brand);
  // if neither clears WCAG AA against the actual secondary surface, fall back
  // to synthesized black/white based on the surface's relative luminance so
  // pale-secondary themes flip to dark text and dark-secondary themes stay
  // light. The muted variant reuses the picked color at 78% mix into the
  // secondary background — same technique the theme uses elsewhere.
  const tokens = useTokens();
  const tintedStyle = useMemo<React.CSSProperties | undefined>(() => {
    const secondary = tokens?.color?.secondary;
    if (!secondary) return undefined;
    const onPrimary = tokens?.color?.textOnPrimary;
    const onSecondary = tokens?.color?.textOnSecondary;
    const candidates = [onSecondary, onPrimary].filter(
      (c): c is string => typeof c === 'string' && c.length > 0,
    );
    const picked = candidates.length ? pickHighestContrast(secondary, candidates) : null;
    const textColor =
      picked && contrastRatio(secondary, picked) >= 4.5
        ? picked
        : relativeLuminance(secondary) > 0.55
          ? '#1a1a1a'
          : '#ffffff';
    return {
      ['--fp-tinted-text' as string]: textColor,
      // Muted lines: soften by mixing 22% of the surface color into the text,
      // matching the previous `text-white/80` feel while still auto-adapting.
      ['--fp-tinted-text-muted' as string]: `color-mix(in srgb, ${textColor} 78%, ${secondary})`,
    } as React.CSSProperties;
  }, [tokens]);

  useRevealStyles();
  useSiteCtaStyles();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  // Scroll-progress fill: bar grows from ~visible-ratio at scrollLeft=0 up
  // to 100% at the end, so it reads as "share of content seen". `scrollable`
  // gates the whole indicator (and auto-scroll) when everything fits.
  const [scroll, setScroll] = useState<{ fill: number; scrollable: boolean }>({
    fill: 100,
    scrollable: false,
  });

  // Which card is currently snapped into view. On mobile each card is 100% of
  // the scroller width, so `scrollLeft / clientWidth` gives the card index.
  // We only *use* this value on mobile — see `featuredIndex` below — but it's
  // cheap to compute and functional-setState skips re-renders when unchanged.
  const [snappedIndex, setSnappedIndex] = useState(0);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const max = scrollWidth - clientWidth;
      const scrollable = max > 0.5;
      const pct = scrollable ? ((scrollLeft + clientWidth) / scrollWidth) * 100 : 100;
      setScroll({ fill: Math.max(0, Math.min(100, pct)), scrollable });
      const idx = clientWidth > 0 ? Math.round(scrollLeft / clientWidth) : 0;
      setSnappedIndex((prev) => (prev === idx ? prev : idx));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [gridRef, categories.length]);

  // Advance the carousel by exactly one card width (plus flex gap). Used by
  // the mobile auto-scroll interval below.
  const scrollByCard = (dir: 1 | -1) => {
    const el = gridRef.current;
    if (!el) return;
    const first = el.querySelector<HTMLElement>('.fp-card-item');
    if (!first) return;
    const styles = getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const step = first.offsetWidth + gap;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  // Pauses the mobile auto-scroll for a short window after any user
  // interaction so the carousel doesn't fight the visitor.
  const [isPaused, setIsPaused] = useState(false);
  const resumeTimerRef = useRef<number | null>(null);
  const pauseAutoScroll = () => {
    setIsPaused(true);
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = window.setTimeout(() => setIsPaused(false), 6000);
  };

  // Mouse-drag scrolling for desktop. Touch already scrolls natively (with
  // momentum + snap), so we only intercept mouse pointers via pointerType.
  // `moved` gates a click-suppression flag so an actual drag doesn't fire
  // the card link on release.
  //
  // NOTE: pointer capture is deferred until movement crosses the drag
  // threshold. If we captured on pointerdown, a plain click on a card would
  // dispatch its pointerup to the captured scroller and the browser would
  // fail to fire `click` on the anchor descendant — links wouldn't navigate.
  const DRAG_THRESHOLD_PX = 6;
  const dragRef = useRef<{
    startX: number;
    startScroll: number;
    moved: boolean;
    pointerId: number;
    captured: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    pauseAutoScroll();
    if (e.pointerType !== 'mouse') return;
    const el = gridRef.current;
    if (!el) return;
    dragRef.current = {
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: false,
      pointerId: e.pointerId,
      captured: false,
    };
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const el = gridRef.current;
    if (!el) return;
    const dx = e.clientX - dragRef.current.startX;
    if (!dragRef.current.moved && Math.abs(dx) > DRAG_THRESHOLD_PX) {
      dragRef.current.moved = true;
      // Only claim pointer capture once we've confirmed a drag, so casual
      // clicks still reach the underlying <a> as normal click events.
      if (!dragRef.current.captured) {
        try {
          el.setPointerCapture(dragRef.current.pointerId);
          dragRef.current.captured = true;
        } catch {
          // Some browsers reject capture on certain pointer types — safe to ignore.
        }
      }
    }
    if (dragRef.current.moved) {
      el.scrollLeft = dragRef.current.startScroll - dx;
    }
  };
  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const el = gridRef.current;
    if (el && dragRef.current.captured && el.hasPointerCapture?.(e.pointerId)) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    }
    if (dragRef.current.moved) suppressClickRef.current = true;
    dragRef.current = null;
  };
  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (suppressClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = false;
    }
  };

  // Track viewport size so auto-scroll only runs on mobile widths.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const hasCategories = categories.length > 0;
  const isScrollable = scroll.scrollable;

  // Which card gets the "tinted" (secondary-surface) treatment. Mobile shows
  // one card at a time, so we follow the snapped card — the visible card
  // always reads as the highlighted one. Tablet + desktop keep the first card
  // tinted as an anchor for the row (hover still swaps the others).
  const featuredIndex = isMobile ? snappedIndex : 0;

  // Mobile auto-scroll: advance one card every 4.5s, wrapping to the first
  // card on reaching the end. Respects prefers-reduced-motion and is paused
  // for 6s after any user interaction (drag, touch, wheel).
  useEffect(() => {
    if (!isMobile || isPaused || !hasCategories || !isScrollable) return;
    const el = gridRef.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = window.setInterval(() => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const max = scrollWidth - clientWidth;
      if (max <= 0) return;
      if (scrollLeft >= max - 1) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollByCard(1);
      }
    }, 4500);
    return () => window.clearInterval(interval);
  }, [isMobile, isPaused, hasCategories, isScrollable, categories.length]);

  return (
    <section className="relative overflow-hidden pt-12 pb-14 md:pt-16 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Left-aligned header block */}
        <div ref={headerRef} className="reveal-fade-up max-w-2xl mx-auto md:mx-0 mb-10 md:mb-12 text-center md:text-left">
          <RichTextContent
            path="floorplanslist.title"
            label="Title"
            as="h2"
            className="font-heading text-4xl sm:text-5xl font-bold text-[var(--vibe-color-primary)] mb-5 leading-[1.05]"
          />
          <RichTextContent
            path="floorplanslist.subtitle"
            label="Subtitle"
            as="p"
            className="font-body text-base sm:text-lg text-[var(--vibe-color-text)] leading-relaxed mb-8 [&_a]:text-[var(--vibe-color-primary)] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:decoration-2"
          />
          <EditableSlotGroup groupPath="floorplanslist.cta_link" definition={ctaLinkDef} className="inline-block">
            <a
              href={cta.url || '/floor-plans'}
              target={cta._target || '_self'}
              className="site-cta site-cta--secondary"
              style={{
                // Swap the default and hover color pairs for this CTA:
                // base state uses the primary accent, hover flips to secondary.
                ['--site-cta-secondary-accent' as string]: 'var(--vibe-color-primary)',
                ['--site-cta-secondary-on-accent' as string]:
                  'var(--vibe-color-text-on-primary, #ffffff)',
                ['--site-cta-hover-accent' as string]: 'var(--vibe-color-secondary)',
                ['--site-cta-hover-on-accent' as string]:
                  'var(--vibe-color-text-on-secondary, #ffffff)',
              }}
            >
              {cta.label || 'View Availability'}
            </a>
          </EditableSlotGroup>
        </div>

        {hasCategories ? (
          <div
            ref={gridRef}
            className={`fp-scroller flex gap-5 md:gap-6 overflow-x-auto pb-6 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory ${
              isScrollable ? 'cursor-grab active:cursor-grabbing select-none' : ''
            }`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            onWheel={pauseAutoScroll}
            onClickCapture={handleClickCapture}
          >
            {/* Bedroom category cards — each uses a literal groupPath for static linter detection.
                `tinted` follows `featuredIndex` so mobile highlights the snapped card while
                tablet + desktop keep the first card as the anchor. */}
            {categories.length > 0 && (
              <FloorplanCard cat={categories[0]} tinted={featuredIndex === 0} tintedStyle={tintedStyle}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_0" definition={def0} className="absolute inset-0">
                  <CardImageContent img={img0} fallbackAlt={`${categories[0].label} floor plans`} />
                </EditableSlotGroup>
              </FloorplanCard>
            )}
            {categories.length > 1 && (
              <FloorplanCard cat={categories[1]} tinted={featuredIndex === 1} tintedStyle={tintedStyle}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_1" definition={def1} className="absolute inset-0">
                  <CardImageContent img={img1} fallbackAlt={`${categories[1].label} floor plans`} />
                </EditableSlotGroup>
              </FloorplanCard>
            )}
            {categories.length > 2 && (
              <FloorplanCard cat={categories[2]} tinted={featuredIndex === 2} tintedStyle={tintedStyle}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_2" definition={def2} className="absolute inset-0">
                  <CardImageContent img={img2} fallbackAlt={`${categories[2].label} floor plans`} />
                </EditableSlotGroup>
              </FloorplanCard>
            )}
            {categories.length > 3 && (
              <FloorplanCard cat={categories[3]} tinted={featuredIndex === 3} tintedStyle={tintedStyle}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_3" definition={def3} className="absolute inset-0">
                  <CardImageContent img={img3} fallbackAlt={`${categories[3].label} floor plans`} />
                </EditableSlotGroup>
              </FloorplanCard>
            )}
            {categories.length > 4 && (
              <FloorplanCard cat={categories[4]} tinted={featuredIndex === 4} tintedStyle={tintedStyle}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_4" definition={def4} className="absolute inset-0">
                  <CardImageContent img={img4} fallbackAlt={`${categories[4].label} floor plans`} />
                </EditableSlotGroup>
              </FloorplanCard>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}

        {/* Scroll-progress indicator — fills in as the carousel advances. */}
        {hasCategories && isScrollable && (
          <div
            className="mt-6 flex justify-center"
            role="presentation"
            aria-hidden="true"
          >
            <div className="relative h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-[var(--vibe-color-primary)]/15">
              <div
                className="h-full rounded-full bg-[var(--vibe-color-primary)] transition-[width] duration-300 ease-out"
                style={{ width: `${scroll.fill}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <style>{`
        /* Horizontal carousel of floor plan cards */
        /* Mobile: one card at a time, centered on snap */
        .fp-scroller .fp-card-item {
          flex: 0 0 100%;
          scroll-snap-align: center;
        }
        /* Tablet: two cards per row (wider than desktop's third-width) */
        @media (min-width: 768px) {
          .fp-scroller .fp-card-item {
            flex: 0 0 calc((100% - 1.5rem) / 2);
            scroll-snap-align: start;
          }
        }
        /* Desktop: three cards per row */
        @media (min-width: 1024px) {
          .fp-scroller .fp-card-item {
            flex: 0 0 calc((100% - 3rem) / 3);
          }
        }
        /* Native scrollbar hidden — a custom progress indicator renders below. */
        .fp-scroller {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .fp-scroller::-webkit-scrollbar {
          height: 0;
          width: 0;
          display: none;
        }
        /* In edit mode, keep the media slot groups clickable. */
        [data-vibesite-edit-mode="true"] .fp-card-media {
          pointer-events: auto;
        }
        /* Tablet + desktop hover: cards flip to their alternate surface —
           non-active cards adopt the tinted look, and the active (tinted)
           card flips back to the default look. The (hover: hover) gate is
           intentionally dropped so touchscreen tablets also see the swap
           briefly on tap. The tinted text CSS vars are already present on
           every card, so we only need to swap the surface + text here. */
        @media (min-width: 768px) {
          .fp-card[data-fp-tinted="hover"]:hover {
            background-color: var(--vibe-color-secondary);
            border-color: var(--vibe-color-secondary);
          }
          .fp-card[data-fp-tinted="hover"]:hover .fp-card-title {
            color: var(--fp-tinted-text, var(--vibe-color-primary));
          }
          .fp-card[data-fp-tinted="hover"]:hover .fp-card-subtext {
            color: var(--fp-tinted-text-muted, var(--vibe-color-text-muted));
          }
          /* Inverse hover on the currently-tinted card: flip back to the
             default surface so every card gives visible hover feedback. */
          .fp-card[data-fp-tinted="active"]:hover {
            background-color: var(--vibe-color-background);
            border-color: color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
          }
          .fp-card[data-fp-tinted="active"]:hover .fp-card-title {
            color: var(--vibe-color-primary);
          }
          .fp-card[data-fp-tinted="active"]:hover .fp-card-subtext {
            color: var(--vibe-color-text-muted);
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Spacious floor plans</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Choose from a range of thoughtfully designed floor plans with private bedrooms, spacious common areas, and modern finishes that make life easier. Explore the layouts below and see how comfort meets convenience.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.card_image_0': cardImage0Def,
    'floorplanslist.card_image_1': cardImage1Def,
    'floorplanslist.card_image_2': cardImage2Def,
    'floorplanslist.card_image_3': cardImage3Def,
    'floorplanslist.card_image_4': cardImage4Def,
    'floorplanslist.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
